import { useRef } from 'react'
import { useIntl } from 'react-intl'
import TextButton from 'LuxKit/Button/TextButton'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import LineCalendarAltIcon from 'LuxKit/Icons/line/LineCalendarAltIcon'
import useToggle from 'hooks/useToggle'
import type { ApiEventDay } from 'leLive/api/leLive'
import { useLeLiveCalendar } from 'leLive/hooks/useLeLiveCalendar'
import { getCalendarDownloadFilename } from 'leLive/utils/calendarUtils'

interface Props {
  eventDay?: ApiEventDay
  kind?: 'primary' | 'secondary' | 'tertiary'
  size?: 'small' | 'medium' | 'large'
  fit?: 'mobile-full-width' | 'full-width' | 'auto'
}

function LeLiveAddToCalendar({
  eventDay,
  kind = 'secondary',
  size = 'large',
  fit = 'mobile-full-width',
}: Props) {
  const intl = useIntl()
  const buttonRef = useRef<HTMLButtonElement>(null)
  const { value: dropdownOpen, toggle: toggleDropdown, off: closeDropdown } = useToggle()
  const { icalBlobUrl, googleCalendarUrl, outlookCalendarUrl } = useLeLiveCalendar(eventDay)
  const calendarDownloadFilename = getCalendarDownloadFilename()

  return (
    <>
      <TextButton
        kind={kind}
        size={size}
        fit={fit}
        startIcon={<LineCalendarAltIcon />}
        ref={buttonRef}
        onClick={toggleDropdown}
      >
        {intl.formatMessage({
          id: 'd3reCYWNt8SwDZ63X3DR',
          defaultMessage: 'Add to calendar',
          description: 'Button label to open the add-to-calendar dropdown in the LE Live event details section',
        })}
      </TextButton>
      <DropdownList
        size="S"
        anchorRef={buttonRef}
        triggerRef={buttonRef}
        open={dropdownOpen}
        onClose={closeDropdown}
        placement="top"
      >
        <ClickableListItem
          title={intl.formatMessage({
            id: 'xEBtcPtkaR42pTjsx5rr',
            defaultMessage: 'Google Calendar',
            description: 'Dropdown menu option to add the LE Live event to Google Calendar from the event details section',
          })}
          href={googleCalendarUrl}
          target="_blank"
          onClick={closeDropdown}
        />
        <ClickableListItem
          title={intl.formatMessage({
            id: 'wiTqxSSrOScfS_CYiyWH',
            defaultMessage: 'Outlook',
            description: 'Dropdown menu option to add the LE Live event to Outlook Calendar from the event details section',
          })}
          href={outlookCalendarUrl}
          target="_blank"
          onClick={closeDropdown}
        />
        <ClickableListItem
          title={intl.formatMessage({
            id: 'h3so7ya93dejeoppjUCu',
            defaultMessage: 'Download event calendar',
            description: 'Dropdown menu option to download an .ics calendar file for the LE Live event from the details section',
          })}
          href={icalBlobUrl}
          download={calendarDownloadFilename}
          onClick={closeDropdown}
        />
      </DropdownList>
    </>
  )
}

export default LeLiveAddToCalendar
